/**
 * Batch Export and batch Run workflow dialogs for the Library's selection (1.2.0); the selection bar (S) opens them.
 * Both take the selected assets as a prop, run the per-asset preflight (useBatchPreflight) and list what will be
 * skipped and why before anything starts; the start acts on the eligible ones and the dialog then shows
 * "Started 18 · skipped 2" with the reasons. At most BULK_ACTION_LIMIT assets at a time, and at most that many batch
 * exports (or batch runs) in progress per person: the server answers 422 TOO_MANY_ASSETS / 429 TOO_MANY_RUNNING, and
 * assets left when the limit is reached meanwhile are skipped as over the limit.
 */
import { useState } from 'react';
import { AlertTriangle, Check, Loader2, Play, Share, Workflow } from 'lucide-react';
import {
  BULK_ACTION_LIMIT, tooManyAssetsMessage, type AssetSummary, type BatchPreflightResult, type BatchResult, type ExportFormatId, type MasterChoice,
} from '@prism/contracts';
import { errorMessage } from '../api/client';
import { useBatchExport, useBatchPreflight, useBatchRunWorkflow, useExportDestinations, useMe, useRunnableWorkflows } from '../api/hooks';
import { Modal } from './Modal';
import { PresetSelect } from './PresetSelect';
import { DestinationSelect } from './DestinationSelect';
import { MasterPicker } from './MasterPicker';
import { toast } from './Toasts';
import { SidecarFormats, sidecarFormatsField } from '../subtitles/SidecarFormats';
import { DEFAULT_SIDECAR_FORMATS, type SidecarFormat } from '@prism/contracts';
import { RightsCheckBlock, useRightsCheck, type PreflightProblem } from './Preflight';
import { batchSummaryText, exportPreflightParams, preflightTally, runPreflightParams, skippedByReason, startableCount, tallyText } from './batch-summary';

export interface BatchDialogProps {
  selectedAssets: AssetSummary[];
  onClose: () => void;
  /** After a start: what started (the selection bar may clear the selection). */
  onDone?: (started: number) => void;
}

/** The per-asset preflight: each asset with a tick, or what stops it. */
function PreflightList({ q, assets }: { q: ReturnType<typeof useBatchPreflight>; assets: AssetSummary[] }) {
  if (q.isLoading) return <div className="faint batch-checking" data-testid="batch-preflight-checking"><Loader2 size={12} className="spin" /> Checking each asset…</div>;
  if (q.error) return <div className="banner error" role="alert" data-testid="batch-preflight-error"><AlertTriangle size={15} /> <div className="banner-body">{errorMessage(q.error)}</div></div>;
  const items = q.data?.items ?? [];
  const titles = new Map(assets.map((a) => [a.id, a.title]));
  return (
    <div className="batch-list" data-testid="batch-preflight">
      {items.map((i) => (
        <div key={i.assetId} className="batch-item" data-testid="batch-preflight-item" data-ok={i.ok} data-restorable={i.restorable ? 'true' : undefined}>
          <span className={`batch-icon ${i.ok ? 'ok' : i.restorable ? 'warn' : 'bad'}`}>{i.ok ? <Check size={12} strokeWidth={3} /> : <AlertTriangle size={12} />}</span>
          <div className="batch-main">
            <span className="batch-title">{i.title ?? titles.get(i.assetId) ?? 'Unknown asset'}</span>
            {i.problems.map((p, n) => <span key={n} className={`batch-reason${p.severity && p.severity !== 'block' ? ' warn' : ''}`} data-code={p.code} data-severity={p.severity}>{p.message}</span>)}
          </div>
        </div>
      ))}
    </div>
  );
}

/** After a start: "Started 18 · skipped 2" and the reasons. */
function ResultSummary({ r, what }: { r: BatchResult; what: string }) {
  const groups = skippedByReason(r.skipped);
  return (
    <div className="batch-result" data-testid="batch-result">
      <div className={`banner ${r.skipped.length ? 'warning' : 'info'}`} role="status">
        {r.skipped.length ? <AlertTriangle size={15} /> : <Check size={15} />}
        <div className="banner-body">
          <strong data-testid="batch-result-summary">{batchSummaryText(r)}</strong>
          {r.started.length > 0 && <span className="muted">Follow the {what} in Activity.</span>}
        </div>
      </div>
      {groups.length > 0 && (
        <div className="batch-list" data-testid="batch-skipped">
          {groups.map((g) => (
            <div key={`${g.code}:${g.message}`} className="batch-item" data-code={g.code}>
              <span className="batch-icon bad"><AlertTriangle size={12} /></span>
              <div className="batch-main">
                <span className="batch-title">{g.items.map((s) => s.title ?? 'Unknown asset').join(', ')}</span>
                <span className="batch-reason">{g.items.length > 1 && g.code !== 'TOO_MANY_RUNNING' && g.code !== 'NOT_FOUND' ? g.message : g.items[0]!.message}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TooMany({ n }: { n: number }) {
  return <p className="banner error" role="alert" data-testid="batch-too-many">{tooManyAssetsMessage()} ({n} selected).</p>;
}

/** 1.8.0: every item's problems with its asset, for the rights block. */
const batchProblems = (data: BatchPreflightResult | undefined): PreflightProblem[] =>
  (data?.items ?? []).flatMap((i) => i.problems.map((p) => ({ ...p, assetId: i.assetId, title: i.title ?? undefined })));

/** A checkbox shown when some originals are only in an archive. */
function RestoreToggle({ data, restore, setRestore }: { data: BatchPreflightResult | undefined; restore: boolean; setRestore: (v: boolean) => void }) {
  if (!data?.items.some((i) => i.restorable)) return null;
  return (
    <label className="toggle-row batch-restore" data-testid="batch-restore">
      <input type="checkbox" checked={restore} onChange={(e) => setRestore(e.target.checked)} />
      Restore archived originals first (minutes to hours; those start when they are back)
    </label>
  );
}

/** Export: format (presets or Original format), source (original or proxy), master, whole or programme, destination, sidecar subtitles. */
export function BatchExportDialog({ selectedAssets, onClose, onDone }: BatchDialogProps) {
  const me = useMe().data;
  const canOriginal = Boolean(me?.permissions.includes('asset.download'));
  const ids = selectedAssets.map((a) => a.id);
  const anyVideo = selectedAssets.some((a) => a.mediaKind === 'video' && (a.durationTicks ?? 0) > 0);
  const [preset, setPreset] = useState<ExportFormatId>(anyVideo ? 'h264-1080p' : 'original');
  const original = preset === 'original';
  const [qualityChoice, setQuality] = useState<'original' | 'proxy'>(canOriginal ? 'original' : 'proxy');
  const quality = original ? 'original' : qualityChoice;
  const [range, setRange] = useState<'full' | 'programme'>('full');
  const [destination, setDestination] = useState('');
  const [subs, setSubs] = useState<'none' | 'sidecar'>('none');
  const [sidecars, setSidecars] = useState<SidecarFormat[]>([...DEFAULT_SIDECAR_FORMATS]);
  const [master, setMaster] = useState<MasterChoice | null>(null);
  const [restore, setRestore] = useState(false);
  const [result, setResult] = useState<BatchResult | null>(null);
  const destinations = useExportDestinations().data?.items ?? [];
  const tooMany = ids.length > BULK_ACTION_LIMIT;
  const preflight = useBatchPreflight(result ? null : exportPreflightParams({ assetIds: ids, preset, quality, range, locationId: destination || null, master: quality === 'original' ? master : null }));
  const tally = preflightTally(preflight.data?.items ?? []);
  const n = startableCount(tally, restore);
  const start = useBatchExport();
  const rights = useRightsCheck(batchProblems(preflight.data));

  async function go() {
    try {
      const r = await start.mutateAsync({
        assetIds: ids, preset, quality, range, locationId: destination || null, subtitleMode: subs, restore,
        ...(subs === 'sidecar' && sidecarFormatsField(sidecars) ? { sidecarFormats: sidecarFormatsField(sidecars) } : {}),
        ...(quality === 'original' && master ? { master } : {}), ...rights.field(),
      });
      setResult(r);
      onDone?.(r.started.length);
      toast(batchSummaryText(r));
    } catch (err) {
      toast(rights.takeError(err) ? 'Check the rights first' : errorMessage(err), 'error');
    }
  }

  return (
    <Modal title={`Export ${selectedAssets.length === 1 ? selectedAssets[0]!.title : `${selectedAssets.length} assets`}`} wide onClose={onClose} actions={<>
      <button className="btn ghost" onClick={onClose}>{result ? 'Done' : 'Cancel'}</button>
      {!result && (
        <button className="btn primary" disabled={tooMany || start.isPending || preflight.isLoading || n === 0 || (!canOriginal && quality === 'original') || !rights.ready} onClick={() => void go()} data-testid="batch-export-start">
          {start.isPending ? <Loader2 size={14} className="spin" /> : <Share size={14} />} Export {n || ''}
        </button>
      )}
    </>}>
      {tooMany ? <TooMany n={ids.length} /> : result ? <ResultSummary r={result} what="exports" /> : <>
        <div className="field-row">
          <div className="field" style={original ? { gridColumn: '1 / -1' } : undefined}><label htmlFor="bx-preset">Format</label>
            <PresetSelect<ExportFormatId> id="bx-preset" value={preset} onChange={(p) => { setPreset(p); if (p === 'original') setRange('full'); }}
              original={canOriginal ? '' : null} originalOnly={!anyVideo} originalHelp="Each original in its own format, byte for byte; assets that are not video can only be exported this way" />
          </div>
          {!original && (
            <div className="field"><label>Source</label>
              <div className="segmented" role="radiogroup" data-testid="batch-export-source">
                <button aria-pressed={quality === 'original'} disabled={!canOriginal} onClick={() => setQuality('original')} title={canOriginal ? undefined : 'Exporting from the original needs the Download originals permission'}>Original</button>
                <button aria-pressed={quality === 'proxy'} onClick={() => setQuality('proxy')}>Proxy (review)</button>
              </div>
            </div>
          )}
        </div>
        <div className="field-row">
          <div className="field"><label>What</label>
            <div className="segmented" role="radiogroup" aria-label="What to export" data-testid="batch-export-what">
              <button aria-pressed={range === 'full'} onClick={() => setRange('full')}>Whole assets</button>
              <button aria-pressed={range === 'programme'} onClick={() => setRange('programme')} data-testid="batch-range-programme"
                title="Between each asset's programme in and out timecodes; assets without them are skipped">Programme</button>
            </div>
            <span className="help">Marked ranges and burnt-in subtitles are set per asset: export those from the asset page.</span>
          </div>
          {quality === 'original' && (
            <div className="field"><label htmlFor="bx-master">Master</label>
              <MasterPicker id="bx-master" value={master} onChange={(v) => setMaster(v && v.type !== 'all' ? v : null)} closestTo={original ? null : preset}
                defaultLabel={original ? 'Primary master' : 'Programme, closest format'} testId="batch-export-master" />
            </div>
          )}
        </div>
        <div className="field-row">
          <div className="field"><label htmlFor="bx-dest">Export to</label>
            <DestinationSelect id="bx-dest" value={destination} onChange={setDestination} destinations={destinations} />
          </div>
          <div className="field"><label htmlFor="bx-subs">Subtitles</label>
            <select id="bx-subs" className="select" value={subs} onChange={(e) => setSubs(e.target.value as typeof subs)} data-testid="batch-export-subs">
              <option value="none">None</option>
              <option value="sidecar">Sidecar files from each asset's first track</option>
            </select>
            {subs === 'sidecar' && <span className="help">Assets without subtitles are exported without them.</span>}
          </div>
          {subs === 'sidecar' && <SidecarFormats value={sidecars} onChange={setSidecars} testId="batch-export-sidecars" />}
        </div>
        <p className="section-label" data-testid="batch-tally">{preflight.data ? tallyText(tally, 'Will export', restore) : 'Assets'}</p>
        <PreflightList q={preflight} assets={selectedAssets} />
        <RightsCheckBlock check={rights} />
        <RestoreToggle data={preflight.data} restore={restore} setRestore={setRestore} />
      </>}
    </Modal>
  );
}

/** One runnable workflow, chosen by a radio button. */
function WorkflowChoice({ w, chosen, onChoose }: { w: { id: string; name: string; description: string; steps: string[] }; chosen: boolean; onChoose: () => void }) {
  return (
    <label className={`batch-item batch-workflow ${chosen ? 'chosen' : ''}`} data-testid="batch-workflow" data-name={w.name}>
      <input type="radio" name="batch-workflow" checked={chosen} onChange={onChoose} />
      <span className="batch-icon"><Workflow size={12} /></span>
      <div className="batch-main">
        <span className="batch-title">{w.name}</span>
        <span className="batch-reason muted">{w.description || w.steps.join(' → ') || 'No steps'}</span>
      </div>
    </label>
  );
}

/** Run workflow: the runnable workflows on assets (useRunnableWorkflows), each with its per-asset preflight. */
export function BatchRunWorkflowDialog({ selectedAssets, onClose, onDone }: BatchDialogProps) {
  const ids = selectedAssets.map((a) => a.id);
  const workflows = useRunnableWorkflows('asset');
  const items = workflows.data?.items ?? [];
  const [chosenId, setChosen] = useState<string | null>(null);
  const chosen = items.find((w) => w.id === chosenId) ?? (items.length === 1 ? items[0] : undefined);
  const [restore, setRestore] = useState(false);
  const [result, setResult] = useState<BatchResult | null>(null);
  const tooMany = ids.length > BULK_ACTION_LIMIT;
  const preflight = useBatchPreflight(result ? null : runPreflightParams(chosen?.id ?? null, ids));
  const tally = preflightTally(preflight.data?.items ?? []);
  const n = startableCount(tally, restore);
  const start = useBatchRunWorkflow();
  const rights = useRightsCheck(batchProblems(preflight.data));

  async function go() {
    if (!chosen) return;
    try {
      const r = await start.mutateAsync({ workflowId: chosen.id, body: { assetIds: ids, restore, ...rights.field() } });
      setResult(r);
      onDone?.(r.started.length);
      toast(`“${chosen.name}”: ${batchSummaryText(r)}`);
    } catch (err) {
      toast(rights.takeError(err) ? 'Check the rights first' : errorMessage(err), 'error');
    }
  }

  return (
    <Modal title={`Run a workflow on ${selectedAssets.length === 1 ? selectedAssets[0]!.title : `${selectedAssets.length} assets`}`} wide onClose={onClose} actions={<>
      <button className="btn ghost" onClick={onClose}>{result ? 'Done' : 'Cancel'}</button>
      {!result && items.length > 0 && (
        <button className="btn primary" disabled={tooMany || !chosen || start.isPending || preflight.isLoading || n === 0 || !rights.ready} onClick={() => void go()} data-testid="batch-run-start">
          {start.isPending ? <Loader2 size={14} className="spin" /> : <Play size={14} />} Run on {n || ''}
        </button>
      )}
    </>}>
      {tooMany ? <TooMany n={ids.length} /> : result ? <ResultSummary r={result} what="runs" /> : workflows.isLoading ? <div className="skeleton" style={{ height: 80 }} /> : items.length === 0 ? (
        <p className="muted" style={{ margin: 0 }} data-testid="batch-no-workflows">
          No workflows can be started on assets yet. In Flows, publish a workflow whose trigger is “Manual run”, and enable it.
        </p>
      ) : <>
        <div className="batch-list" data-testid="batch-workflows">
          {items.map((w) => <WorkflowChoice key={w.id} w={w} chosen={chosen?.id === w.id} onChoose={() => setChosen(w.id)} />)}
        </div>
        {chosen ? <>
          <p className="section-label" data-testid="batch-tally">{preflight.data ? tallyText(tally, 'Will run on', restore) : 'Assets'}</p>
          <PreflightList q={preflight} assets={selectedAssets} />
          <RightsCheckBlock check={rights} />
          <RestoreToggle data={preflight.data} restore={restore} setRestore={setRestore} />
        </> : <p className="muted">Choose a workflow to check the assets.</p>}

      </>}
    </Modal>
  );
}
